// Pure helpers for the Swap Curve widget — swap detection and block-id extraction.
// No JSX, no React — keep as .ts and unit-test independently.

import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import { Role } from "@/lib/interfaces and Enums/ENUMRole";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { SecurityTypeCode } from "@/lib/interfaces and Enums/ENUMSecurityTypeCode";
import { IImportJsonBlocks } from "@/store/importBlocksAndAllocationsStore";
import { IBlockAssigned } from "@/store/tradeBlockDataStore";

// Where an entry on the Swap Curve came from.
export type SwapCurveSource = "Trade" | "Block";

// One item added to the widget. `blockIds` is what eventually gets sent to the back end.
export interface SwapCurveEntry {
  source: SwapCurveSource;
  sourceId: string; // esid (Trade) or block id as string (Block) — unique key + display
  label: string; // human-friendly label (esid or securityName)
  blockIds: number[]; // swap block id(s) contributed to the back-end payload
}

// Anything carrying the swap-classification fields — a Creation block OR a trade leg.
// (A leg IS a block; see ExecutionRequestQuoteLeg.) Trade blocks/legs carry instrumentType
// "SWP"; Creation blocks identify the structure via securityType. We check both.
interface SwapClassifiable {
  instrumentType?: string;
  securityType?: string;
}

// securityType codes that represent a swap. Adjust here if more swap structures should be
// treated as swap-curve eligible (e.g. SWO, IBS, XBS).
export const SWAP_SECURITY_TYPES: string[] = [
  SecurityTypeCode.IRS, // Interest Rate Swap
  SecurityTypeCode.OIS, // Overnight Index Swap
  SecurityTypeCode.ZCS, // Zero Coupon Swap
  SecurityTypeCode.XCS, // Cross Currency Swap
];

// True when an instrument-type code represents a swap.
export const isSwapInstrumentType = (instrumentType?: string): boolean =>
  instrumentType === InstrumentTypeCode.SWP;

// True when a security-type code represents a swap.
export const isSwapSecurityType = (securityType?: string): boolean =>
  !!securityType && SWAP_SECURITY_TYPES.includes(securityType);

// True when a block/leg is a swap by either signal. Null-safe.
export const isSwapBlock = (block?: SwapClassifiable | null): boolean => {
  if (!block) return false;
  return (
    isSwapInstrumentType(block.instrumentType) ||
    isSwapSecurityType(block.securityType)
  );
};

// A trade's blocks are modelled as "legs" (ExecutionRequestQuoteLeg), and they live in a
// different place depending on the trade's side:
//   - RP: directly on `trade.legs`
//   - OP: on each counterparty, `trade.executionRequestCounterParties[].legs`
// Structure vs outright only changes the number of legs (outright = one), so iterating the
// array covers both. For OP, every CP quotes the same legs, so callers must dedupe by blockId.
export const getTradeLegs = (
  trade?: ExecutionStrategy | null,
): ExecutionRequestQuoteLeg[] => {
  if (!trade) return [];
  if (trade.legs && trade.legs.length > 0) return trade.legs; // RP
  if (
    trade.executionRequestCounterParties &&
    trade.executionRequestCounterParties.length > 0
  ) {
    return trade.executionRequestCounterParties.flatMap((cp) => cp.legs ?? []); // OP
  }
  return [];
};

// The unique block ids of every swap leg in a trade. Empty when the trade has no swap legs.
export const extractSwapBlockIdsFromTrade = (
  trade?: ExecutionStrategy | null,
): number[] => {
  const swapBlockIds = getTradeLegs(trade)
    .filter((leg) => isSwapBlock(leg))
    .map((leg) => leg.blockId)
    .filter(
      (blockId): blockId is number => blockId !== undefined && blockId !== null,
    );
  return Array.from(new Set(swapBlockIds));
};

// True when a trade contains at least one swap leg.
export const tradeContainsSwap = (trade?: ExecutionStrategy | null): boolean =>
  extractSwapBlockIdsFromTrade(trade).length > 0;

// ---- Indicative mid overlay (OP trades only) -------------------------------
// One swap block's indicative mid, keyed by its (structure) block, plus the swap's tenor date
// (the quote leg's `maturity`, LQC-1898) so the chart can place each marker at its own maturity on
// the x-axis. `tenorDate` is undefined when the leg has no maturity - the chart then falls back to
// the earliest tenor.
export interface IndicativeMidMarker {
  blockId: number;
  mid: number;
  tenorDate?: Date;
  securityName?: string; // what the tooltip shows; blockId is only the fallback
  // The leg's quote notation. Picks the Y axis: % on Zero Rate, bps on Spread (LQC-2883).
  notation?: string;
}

// What a tooltip calls a swap block: the security name it carries, with the block id as the
// fallback for the payloads that supply neither name. Shared by the mid and counterparty tooltips.
export const securityLabel = (datum?: {
  securityName?: string;
  blockId?: number;
}): string => datum?.securityName || `Block ${datum?.blockId}`;

// The human-readable name of the security a leg trades. Real payloads populate `security`;
// `securityName` is the documented-but-often-absent alternative. Undefined when neither is set,
// so callers fall back to the block id.
export const legSecurityName = (
  leg?: ExecutionRequestQuoteLeg | null,
): string | undefined => {
  const name = leg?.security || leg?.securityName;
  return name?.trim() ? name.trim() : undefined;
};

// Swap legs quoted in "%" share the curve's zero-rate y-axis. Anything else (a "bps" structure
// spread, "$fr", ...) is a different scale and cannot be plotted against it. Missing notation is
// allowed through - the parse still rejects anything non-numeric.
export const isPercentRateNotation = (notation?: string): boolean =>
  !notation || notation === "%";

// Which Y axis a quote belongs on (LQC-2883): "%" rates share the curve's Zero Rate axis, "bps"
// spreads go on the right-hand Spread axis. Undefined for any other unit, which is not plotted.
export type SwapCurveYAxis = "rate" | "spread";
export const yAxisForNotation = (
  notation?: string,
): SwapCurveYAxis | undefined => {
  if (isPercentRateNotation(notation)) return "rate";
  if (notation === "bps") return "spread";
  return undefined;
};

// AG Charts binds a series to an axis by its yKey, so spread series plot a separate key. The
// tooltip-facing `mid` / `quote` stay on every point.
export const spreadYKey = (key: "mid" | "quote"): string => `${key}Bps`;
export const RATE_AXIS_KEYS = ["zeroRatePercent", "mid", "quote"];
export const SPREAD_AXIS_KEYS = [spreadYKey("mid"), spreadYKey("quote")];

// A quote in its own unit for tooltips: "3.190%" on the rate axis, "20.831 bps" on the spread axis.
export const formatQuoteWithUnit = (
  value: number,
  notation?: string,
): string =>
  yAxisForNotation(notation) === "spread"
    ? `${Number(value).toFixed(3)} bps`
    : `${Number(value).toFixed(3)}%`;

// Parse a leg's indicativeMid to a plain number. Only clean numerics pass; fraction strings
// ("116-023") and junk return undefined so a non-rate quote is never plotted on the % axis.
export const parseIndicativeMid = (
  value?: number | string | null,
): number | undefined => {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    return Number(value.trim());
  }
  return undefined;
};

// Parse a leg's maturity (Date or ISO string) to a valid Date, or undefined. Invalid/missing
// dates return undefined so a marker never anchors to NaN on the time axis.
export const parseLegMaturityDate = (
  value?: Date | string | null,
): Date | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

// The maturity inside a swap's security name. The API builds every SWP name as
// "{SecType} {Ccy} {dd-MMM-yyyy} to {dd-MMM-yyyy} (Freq: ...)" (SecurityNameBusinessRule), and at
// status New nothing else on the trade carries the maturity (LQC-2908). Parsed by hand because
// browsers disagree on `new Date("08-Feb-2028")`. The server's culture can abbreviate past 3
// letters ("Sept"), so months key on their first 3. Undefined when the name doesn't match.
export const parseSwapMaturityFromSecurityName = (
  securityName?: string | null,
): Date | undefined => {
  const match = securityName?.match(/\bto (\d{1,2})-([A-Za-z]{3,9})-(\d{4})\b/);
  if (!match) return undefined;
  const month = MONTH_INDEX[match[2].slice(0, 3).toLowerCase()];
  if (month === undefined) return undefined;
  const day = Number(match[1]);
  const date = new Date(Date.UTC(Number(match[3]), month, day));
  // Rejects day overflow like 31-Feb, which Date.UTC would roll into March.
  return date.getUTCDate() === day ? date : undefined;
};

// Status New markers from the trade's assigned blocks (`/Blocks/Assigned/{esid}`), which carry a
// mid, notation and security name per block where the summary's structure legs carry none
// (LQC-2908). One marker per swap block, so Structures and multi-swap trades plot too.
export const extractAssignedBlockIndicativeMids = (
  trade: ExecutionStrategy | null | undefined,
  swapBlockIds: number[],
  assignedBlocks: IBlockAssigned[],
): IndicativeMidMarker[] => {
  if (!trade || isResponderTrade(trade) || trade.unwind === true) return [];
  const structureLegs = getTradeLegs(trade);

  return swapBlockIds.reduce<IndicativeMidMarker[]>((markers, blockId) => {
    const block = assignedBlocks.find((b) => b.id === blockId);
    const mid = parseIndicativeMid(block?.indicativeMid);
    if (!block || mid === undefined) return markers;
    const securityName = block.securityName?.trim() || undefined;
    // A structure leg `maturity` wins once the API sends one at New; the name parse is the fallback.
    const legMaturity = structureLegs.find(
      (l) => l.blockId === blockId,
    )?.maturity;
    markers.push({
      blockId,
      mid,
      tenorDate:
        parseLegMaturityDate(legMaturity) ??
        parseSwapMaturityFromSecurityName(securityName),
      securityName,
      notation: block.notation,
    });
    return markers;
  }, []);
};

// True when the trade has counterparty quote legs to read mids from. False at status New, before
// any execution request exists.
export const hasCounterpartyQuoteLegs = (
  trade?: ExecutionStrategy | null,
): boolean =>
  (trade?.executionRequestCounterParties?.[0]?.legs?.length ?? 0) > 0;

// Last-resort fallback while the assigned blocks are still loading. The trade-level
// `indicativeMid` (LQC-2865) is ONE value for the whole strategy, so it is only safe to plot on an
// Outright single-swap trade: a Structure's value is a spread and an unwind's is NPV-style. With
// no maturity or name, the marker sits at the earliest tenor and is labelled by block id.
export const extractTradeLevelIndicativeMid = (
  trade: ExecutionStrategy | null | undefined,
  swapBlockIds: number[],
): IndicativeMidMarker[] => {
  if (!trade || isResponderTrade(trade)) return [];
  if (swapBlockIds.length !== 1) return [];
  if (trade.strategyType !== StrategyType.Outright) return [];
  if (trade.unwind === true) return [];

  const mid = parseIndicativeMid(trade.indicativeMid);
  return mid === undefined
    ? []
    : [{ blockId: swapBlockIds[0], mid, notation: trade.notationES }];
};

// Indicative mids for the given swap blocks. The swap blocks come from the trade's structure legs
// (getTradeLegs - top-level when present), which carry each block's `leg` ordinal. The mid and
// `maturity` live on the FIRST counterparty's quote legs (identical across CPs), under a DIFFERENT
// blockId - so the two are correlated by `leg`, NOT blockId (LQC-1898). The marker keeps the
// structure block id (what the curve is fetched for) for display; tenorDate is the quote leg's
// `maturity` (undefined -> chart falls back to the earliest tenor). Empty for non-OP trades.
// With no CP legs (status New) the mids come from `assignedBlocks`, or the trade-level mid until
// those have loaded.
export const extractIndicativeMidsFromTrade = (
  trade: ExecutionStrategy | null | undefined,
  swapBlockIds: number[],
  assignedBlocks?: IBlockAssigned[],
): IndicativeMidMarker[] => {
  const cpLegs = trade?.executionRequestCounterParties?.[0]?.legs;
  if (!cpLegs || cpLegs.length === 0) {
    return assignedBlocks && assignedBlocks.length > 0
      ? extractAssignedBlockIndicativeMids(trade, swapBlockIds, assignedBlocks)
      : extractTradeLevelIndicativeMid(trade, swapBlockIds);
  }

  // Same legs the swapBlockIds were derived from, so each block's `leg` ordinal ties to a quote leg.
  const structureLegs = getTradeLegs(trade);

  return swapBlockIds.reduce<IndicativeMidMarker[]>((markers, blockId) => {
    const legValue = structureLegs.find((l) => l.blockId === blockId)?.leg;
    if (legValue === undefined || legValue === null) return markers;

    // blockIds differ between structure and quote legs; `leg` is the shared key. Compare as strings
    // since `leg` can arrive as a number or a string depending on the payload.
    const cpLeg = cpLegs.find((l) => String(l.leg) === String(legValue));
    const mid = parseIndicativeMid(cpLeg?.indicativeMid);
    if (mid !== undefined) {
      markers.push({
        blockId,
        mid,
        tenorDate: parseLegMaturityDate(cpLeg?.maturity),
        securityName: legSecurityName(cpLeg),
        notation: cpLeg?.notation,
      });
    }
    return markers;
  }, []);
};

// True when the trade is on the responding (RP) side — i.e. the user did NOT originate it.
// Only originated (OP) trades have curve data we can fetch, so callers skip the back-end call
// for RP trades. Null-safe.
export const isResponderTrade = (trade?: ExecutionStrategy | null): boolean =>
  trade?.role === Role.RP;

// ---- Curve data (graphing) -------------------------------------------------
// Shape of one constituent point within a curve's `dataPoints`, as returned by
// GET /Curves/ByBlocks (camelCase keys).
export interface SwapCurveRow {
  curveConstituentName: string;
  pricingDefinitionName: string;
  securityName: string;
  tenorDate: string;
  rate: number;
  zeroRate: number;
  discountFactor: number;
}

// A full curve: metadata shared by every point (lifted out of the per-point rows) plus its
// constituent points.
export interface SwapCurve {
  curveCustomerName: string;
  valuationDate: string;
  dataPoints: SwapCurveRow[];
}

// Display name for a curve — its customer name, with an index fallback. Used as the series
// name/legend label when curves are overlaid on the chart.
export const curveDisplayName = (curve: SwapCurve, index: number): string =>
  curve.curveCustomerName || `Curve ${index + 1}`;

// One plotted point: TenorDate on the (time) X-axis, ZeroRate scaled to a percentage on the
// Y-axis. `tenor`/`securityName` ride along for the tooltip only.
export interface SwapCurvePoint {
  tenorDate: Date;
  zeroRatePercent: number;
  tenor: string;
  securityName: string;
}

// Map raw curve rows to chart points: parse tenorDate to a Date and scale zeroRate to a
// percentage (0.0373266 -> 3.73266). Sorted ascending by tenor date so the line always draws
// left-to-right regardless of the source order.
export const mapSwapCurveRowsToPoints = (
  rows: SwapCurveRow[],
): SwapCurvePoint[] =>
  rows
    .map((row) => ({
      tenorDate: new Date(row.tenorDate),
      zeroRatePercent: row.zeroRate * 100,
      tenor: row.curveConstituentName,
      securityName: row.securityName,
    }))
    .sort((a, b) => a.tenorDate.getTime() - b.tenorDate.getTime());

// Linearly interpolate a curve's zeroRatePercent at an arbitrary date. `points` must be sorted
// ascending by tenorDate (mapSwapCurveRowsToPoints guarantees this). Returns undefined when the
// date is outside the curve's own range (no extrapolation) or there are no points. Used by the
// combined tooltip so every overlaid curve can show a value at the hovered x, even when the
// curves don't share identical tenor dates.
export const interpolateZeroRateAtDate = (
  points: SwapCurvePoint[],
  date: Date,
): number | undefined => {
  if (!points || points.length === 0) return undefined;
  const t = date.getTime();
  const first = points[0].tenorDate.getTime();
  const last = points[points.length - 1].tenorDate.getTime();
  if (t < first || t > last) return undefined;

  for (let i = 0; i < points.length - 1; i++) {
    const ta = points[i].tenorDate.getTime();
    const tb = points[i + 1].tenorDate.getTime();
    if (t === ta) return points[i].zeroRatePercent;
    if (t > ta && t <= tb) {
      if (tb === ta) return points[i + 1].zeroRatePercent;
      const ratio = (t - ta) / (tb - ta);
      const a = points[i].zeroRatePercent;
      const b = points[i + 1].zeroRatePercent;
      return a + ratio * (b - a);
    }
  }
  return points[points.length - 1].zeroRatePercent;
};

// ---- Back-end call ---------------------------------------------------------
// The Swap Curve data endpoint takes the swap block ids as a repeated query param:
//   /Curves/ByBlocks?BlockIds=18107&BlockIds=18108
// Build that relative url (base url is prepended by fetchRequestWrapper). Null/undefined ids
// are dropped so a partial blockIds array can't produce a malformed query.
export const buildCurvesByBlocksUrl = (blockIds: number[]): string => {
  const query = (blockIds ?? [])
    .filter((id): id is number => id !== null && id !== undefined)
    .map((id) => `BlockIds=${encodeURIComponent(id)}`)
    .join("&");
  return `/Curves/ByBlocks?${query}`;
};

// Look up a Creation block by its id (the drag payload carries the id as a string).
export const findBlockById = (
  blocks: IImportJsonBlocks[],
  blockId: string | number | null | undefined,
): IImportJsonBlocks | undefined => {
  if (blockId === null || blockId === undefined || blockId === "")
    return undefined;
  const idNum = Number(blockId);
  if (Number.isNaN(idNum)) return undefined;
  return blocks.find((block) => block.id === idNum);
};
